const KNOWN_SOURCE_PATTERNS = [
  { label: 'Instagram', patterns: ['instagram.com', 'instagr.am'] },
  { label: 'TikTok', patterns: ['tiktok.com'] },
  { label: 'YouTube', patterns: ['youtube.com', 'youtu.be'] },
  { label: 'LinkedIn', patterns: ['linkedin.com'] },
  { label: 'Facebook', patterns: ['facebook.com', 'fb.com', 'fb.me'] },
  { label: 'X / Twitter', patterns: ['twitter.com', 'x.com', 't.co'] },
  { label: 'Threads', patterns: ['threads.net'] },
  { label: 'Reddit', patterns: ['reddit.com'] },
  { label: 'Pinterest', patterns: ['pinterest.com', 'pin.it'] },
  { label: 'Snapchat', patterns: ['snapchat.com', 'snap.com'] },
  { label: 'Discord', patterns: ['discord.gg', 'discord.com'] },
  { label: 'Telegram', patterns: ['t.me', 'telegram.me', 'telegram.org'] },
  { label: 'WhatsApp', patterns: ['wa.me', 'whatsapp.com'] },
  { label: 'Twitch', patterns: ['twitch.tv'] },
  { label: 'Spotify', patterns: ['spotify.com', 'open.spotify.com'] },
  { label: 'Google', patterns: ['google.', 'g.co', 'goo.gl', 'maps.app.goo.gl'] },
  { label: 'Bing', patterns: ['bing.com'] },
  { label: 'DuckDuckGo', patterns: ['duckduckgo.com'] },
];

const STORAGE_KEY = 'linkit_analytics_context_v1';

const safeWindow = () => (typeof window !== 'undefined' ? window : null);

const getHostname = (value = '') => {
  try {
    return new URL(String(value || '').trim()).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const detectAnalyticsPlatform = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = raw.toLowerCase();
  const hostname = getHostname(raw) || normalized.replace(/^www\./, '');
  const match = KNOWN_SOURCE_PATTERNS.find((source) => (
    source.patterns.some((pattern) => hostname.includes(pattern) || normalized.includes(pattern))
  ));

  if (match) return match.label;
  if (hostname) return hostname;
  return raw.slice(0, 80);
};

const readStoredContext = () => {
  const win = safeWindow();
  if (!win?.sessionStorage) return null;
  try {
    return JSON.parse(win.sessionStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const writeStoredContext = (context) => {
  const win = safeWindow();
  if (!win?.sessionStorage) return;
  try {
    win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Storage can be unavailable in private or embedded contexts.
  }
};

export const captureAnalyticsContext = () => {
  const win = safeWindow();
  if (!win) return {};

  const params = new URLSearchParams(win.location.search || '');
  const stored = readStoredContext() || {};
  const rawReferrer = document.referrer || stored.raw_referrer || '';
  const sourcePlatform =
    params.get('utm_source') ||
    stored.source_platform ||
    detectAnalyticsPlatform(rawReferrer) ||
    '';

  const context = {
    page_url: win.location.href,
    raw_referrer: rawReferrer,
    source_platform: sourcePlatform || 'Direct',
    utm_source: params.get('utm_source') || stored.utm_source || '',
    utm_medium: params.get('utm_medium') || stored.utm_medium || '',
    utm_campaign: params.get('utm_campaign') || stored.utm_campaign || '',
  };

  writeStoredContext(context);
  return context;
};

export const getAnalyticsContext = () => {
  const win = safeWindow();
  const params = new URLSearchParams(win?.location?.search || '');
  const stored = readStoredContext() || {};
  const rawReferrer = stored.raw_referrer || (typeof document !== 'undefined' ? document.referrer : '');
  const context = {
    ...stored,
    page_url: win?.location?.href || stored.page_url || '',
    raw_referrer: rawReferrer,
    source_platform: stored.source_platform || params.get('utm_source') || detectAnalyticsPlatform(rawReferrer),
    utm_source: stored.utm_source || params.get('utm_source') || '',
    utm_medium: stored.utm_medium || params.get('utm_medium') || '',
    utm_campaign: stored.utm_campaign || params.get('utm_campaign') || '',
  };
  return {
    page_url: context.page_url,
    raw_referrer: context.raw_referrer,
    source_platform: context.source_platform || detectAnalyticsPlatform(context.raw_referrer) || 'Direct',
    utm_source: context.utm_source,
    utm_medium: context.utm_medium,
    utm_campaign: context.utm_campaign,
  };
};

export const buildAnalyticsPayload = (extra = {}) => ({
  ...getAnalyticsContext(),
  ...extra,
});

export const appendAnalyticsQuery = (href) => {
  const context = getAnalyticsContext();
  try {
    const url = new URL(href, safeWindow()?.location?.origin || 'https://linkit.local');
    if (context.source_platform) url.searchParams.set('source_platform', context.source_platform);
    if (context.utm_source) url.searchParams.set('utm_source', context.utm_source);
    if (context.utm_medium) url.searchParams.set('utm_medium', context.utm_medium);
    if (context.utm_campaign) url.searchParams.set('utm_campaign', context.utm_campaign);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
};
